import React, { useState } from 'react';
import './WaitingListModal.css';
import { aFechaISO } from '../lib/calendario';
import { API_URL } from '../config/api';


const WaitingListModal = ({ 
  isOpen, 
  onClose, 
  businessData, 
  currentBooking, 
  selectedService, // Ya viene seleccionado, no necesita elegir
  staff,
  resources 
}) => {
  const [searchType, setSearchType] = useState('specific'); // 'specific' o 'intelligent'
  
  // Para búsqueda específica
  const [preferredDate, setPreferredDate] = useState('');
  const [preferredTime, setPreferredTime] = useState('');
  
  // Para búsqueda inteligente - SIMPLIFICADO
  const [targetWeekday, setTargetWeekday] = useState('monday'); // día de la semana
  const [beforeDate, setBeforeDate] = useState(''); // antes de esta fecha
  const [timeRangeStart, setTimeRangeStart] = useState('09:00'); // desde hora
  const [timeRangeEnd, setTimeRangeEnd] = useState('17:00'); // hasta hora
  
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [selectedResource, setSelectedResource] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [position, setPosition] = useState(null);
  const [closing, setClosing] = useState(false);

  // Generar horarios disponibles
  const generateTimeSlots = () => {
    const slots = [];
    for (let hour = 6; hour <= 22; hour++) {
      for (let minute = 0; minute < 60; minute += 30) {
        const timeString = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
        slots.push(timeString);
      }
    }
    return slots;
  };

  const timeSlots = generateTimeSlots();

  // Generar fechas futuras (próximos 60 días)
  const generateAvailableDates = () => {
    const dates = [];
    const today = new Date();
    
    for (let i = 1; i <= 60; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      // `date` sale de `new Date(today)`, o sea que lleva la hora actual: con
      // `toISOString` las 60 fechas se corrían un día enteras a partir de las
      // 21:00, y la lista de espera ofrecía días equivocados.
      dates.push(aFechaISO(date));
    }
    
    return dates;
  };

  const availableDates = generateAvailableDates();

  // Días de la semana
  const weekDays = [
    { key: 'monday', label: 'Lunes' },
    { key: 'tuesday', label: 'Martes' },
    { key: 'wednesday', label: 'Miércoles' },
    { key: 'thursday', label: 'Jueves' },
    { key: 'friday', label: 'Viernes' },
    { key: 'saturday', label: 'Sábado' },
    { key: 'sunday', label: 'Domingo' }
  ];

  const handleSubmitWaitingList = async () => {
    // Validaciones según el tipo de búsqueda
    if (searchType === 'specific') {
      if (!preferredDate || !preferredTime) {
        setError('Por favor, completa la fecha y hora específica');
        return;
      }
    } else if (searchType === 'intelligent') {
      if (!targetWeekday || !beforeDate || !timeRangeStart || !timeRangeEnd) {
        setError('Por favor, completa todos los criterios de búsqueda');
        return;
      }
      if (timeRangeStart >= timeRangeEnd) {
        setError('La hora de inicio debe ser menor que la hora de fin');
        return;
      }
    }

    setIsLoading(true);
    setError('');

    try {
      const payload = {
        businessId: businessData.id,
        clientPhone: currentBooking.customer.phone,
        clientName: currentBooking.customer.name,
        clientEmail: currentBooking.customer.email || '',
        currentBookingId: currentBooking.bookingId,
        
        // Tipo de búsqueda
        searchType: searchType,
        
        // Datos para búsqueda específica
        preferredDate: searchType === 'specific' ? preferredDate : null,
        preferredTime: searchType === 'specific' ? preferredTime : null,
        
        // Datos para búsqueda inteligente - SIMPLIFICADO
        targetWeekday: searchType === 'intelligent' ? targetWeekday : null,
        beforeDate: searchType === 'intelligent' ? beforeDate : null,
        timeRangeStart: searchType === 'intelligent' ? timeRangeStart : null,
        timeRangeEnd: searchType === 'intelligent' ? timeRangeEnd : null,
        
        // Servicio ya seleccionado
        preferredService: selectedService.name,
        preferredStaff: selectedStaff?.name || null,
        preferredResource: selectedResource?.name || null
      };

      console.log('📋 FRONTEND: Enviando solicitud de lista de espera:', payload);

      const response = await fetch(`${API_URL}/api/waiting-list`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Error al agregar a lista de espera');
      }

      console.log('✅ FRONTEND: Agregado a lista de espera exitosamente');
      setSuccess(true);
      setPosition(data.position);

    } catch (error) {
      console.error('❌ Error al agregar a lista de espera:', error);
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setClosing(true);
    setTimeout(() => {
      setClosing(false);
      onClose();
    }, 250);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('es-AR', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatIntelligentSearch = () => {
    const dayText = weekDays.find(day => day.key === targetWeekday)?.label;
    return `Primer ${dayText} disponible antes del ${formatDate(beforeDate)}, de ${timeRangeStart} a ${timeRangeEnd}`;
  };

  if (!isOpen && !closing) return null;

  return (
    // `lista-espera-overlay` es el ámbito de WaitingListModal.css. Sin él, sus
    // nombres genéricos —.btn-primary, .form-group, .modal-header, .form-input—
    // le pisaban el estilo a toda la app: CRA junta el CSS en un solo archivo y
    // no acota nada. El síntoma visible era que abajo de 768px todos los botones
    // de la app quedaban a ancho completo por un `min-width: 100%` de acá.
    <div className={`lista-espera-overlay${closing ? ' closing' : ''}`} onClick={handleClose}>
      {/* `data-lenis-prevent` porque este modal tiene su propio scroll
          (max-height: 90vh) y la página pública corre con scroll de inercia:
          lenis se queda con la rueda de toda la ventana, así que sin esto la
          rueda adentro del modal movía la página de atrás y el modal no
          scrolleaba nunca. Mismo caso que DepositPaymentModal. */}
      <div
        className={`waiting-list-modal${closing ? ' closing' : ''}`}
        onClick={e => e.stopPropagation()}
        data-lenis-prevent
      >
        {success ? (
          <div className="waiting-list-success">
            <div className="success-icon">📋</div>
            <h2>¡Ya estás en la Lista de Espera!</h2>
            <p>
              <strong>¿Qué significa esto?</strong><br />
              Si alguien cancela su turno para <strong>{selectedService.name}</strong> y coincide con lo que buscas, te avisaremos inmediatamente por WhatsApp.
            </p>
            <div className="waiting-list-details">
              {searchType === 'specific' ? (
                <>
                  <div className="waiting-detail">
                    <span className="detail-icon">📅</span>
                    <span>Esperando: {formatDate(preferredDate)}</span>
                  </div>
                  <div className="waiting-detail">
                    <span className="detail-icon">⏰</span>
                    <span>A las: {preferredTime}</span>
                  </div>
                </>
              ) : (
                <div className="waiting-detail">
                  <span className="detail-icon">🔍</span>
                  <span>Esperando: {formatIntelligentSearch()}</span>
                </div>
              )}
              <div className="waiting-detail">
                <span className="detail-icon">💼</span>
                <span>Servicio: {selectedService.name}</span>
              </div>
              <div className="waiting-detail">
                <span className="detail-icon">📍</span>
                <span>Tu posición en la lista: #{position}</span>
              </div>
            </div>
            <div className="waiting-list-info">
              <p><strong>📞 ¿Cómo funciona la Lista de Espera?</strong></p>
              <p>
                <strong>1. Alguien cancela:</strong> {searchType === 'specific' 
                  ? 'Si alguien cancela exactamente tu horario preferido'
                  : 'Si encontramos un horario que coincide con tus criterios'
                }, te llamamos por WhatsApp.
              </p>
              <p>
                <strong>2. Tienes 15 minutos:</strong> Para decidir si quieres tomar ese lugar o seguir esperando.
              </p>
              <p>
                <strong>3. Tu turno actual se mantiene:</strong> Sigues teniendo tu reserva original hasta que decidas cambiarte.
              </p>
            </div>
            <button onClick={handleClose} className="btn-primary">
              Entendido
            </button>
          </div>
        ) : (
          <div className="waiting-list-form">
            <div className="modal-header">
              <h2>📋 Lista de Espera</h2>
              <button className="close-btn" onClick={handleClose}>×</button>
            </div>

            {error && <div className="error-message">{error}</div>}

            <div className="step-container">
              <h3>¿Te gustaría estar en lista de espera?</h3>
              <p className="step-description">
                Si alguien cancela su turno para <strong>{selectedService?.name}</strong>, te avisaremos inmediatamente para que puedas tomar ese lugar.
              </p>

              <div className="waiting-list-explanation">
                <div className="explanation-item">
                  <span className="explanation-icon">📞</span>
                  <span>Te llamamos por WhatsApp si se libera un lugar</span>
                </div>
                <div className="explanation-item">
                  <span className="explanation-icon">⏱️</span>
                  <span>Tienes 15 minutos para decidir</span>
                </div>
                <div className="explanation-item">
                  <span className="explanation-icon">🎯</span>
                  <span>Solo te avisamos si coincide con lo que buscas</span>
                </div>
              </div>

              {/* Selector de tipo de búsqueda */}
              <div className="search-type-selector">
                <h4>¿Qué tipo de horario buscas?</h4>
                <div className="search-type-options">
                  <label className="search-type-option">
                    <input 
                      type="radio" 
                      name="searchType" 
                      value="specific"
                      checked={searchType === 'specific'}
                      onChange={(e) => setSearchType(e.target.value)}
                    />
                    <div className="search-type-content">
                      <h4>📅 Horario específico</h4>
                      <p>Quiero un día y hora exacta</p>
                    </div>
                  </label>

                  <label className="search-type-option">
                    <input 
                      type="radio" 
                      name="searchType" 
                      value="intelligent"
                      checked={searchType === 'intelligent'}
                      onChange={(e) => setSearchType(e.target.value)}
                    />
                    <div className="search-type-content">
                      <h4>🔍 Buscar automático</h4>
                      <p>Cualquier día que cumpla mis condiciones</p>
                    </div>
                  </label>
                </div>
              </div>

              {searchType === 'specific' && (
                <div className="search-specific">
                  <h4>¿Cuál es tu horario ideal?</h4>
                  
                  <div className="form-group">
                    <label htmlFor="preferred-date">Fecha:</label>
                    <select 
                      id="preferred-date"
                      value={preferredDate} 
                      onChange={(e) => setPreferredDate(e.target.value)}
                      className="form-select"
                    >
                      <option value="">Elige una fecha</option>
                      {availableDates.map(date => (
                        <option key={date} value={date}>
                          {formatDate(date)}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label htmlFor="preferred-time">Hora:</label>
                    <select 
                      id="preferred-time"
                      value={preferredTime} 
                      onChange={(e) => setPreferredTime(e.target.value)}
                      className="form-select"
                    >
                      <option value="">Elige una hora</option>
                      {timeSlots.map(time => (
                        <option key={time} value={time}>
                          {time}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              )}

              {searchType === 'intelligent' && (
                <div className="search-intelligent">
                  <h4>Te ayudamos a encontrar el horario perfecto</h4>
                  
                  <div className="search-sentence">
                    <div className="sentence-part">
                      <span className="sentence-text">Voy a buscar el primer</span>
                    </div>
                    
                    <div className="sentence-part">
                      <select 
                        value={targetWeekday} 
                        onChange={(e) => setTargetWeekday(e.target.value)}
                        className="form-select-inline"
                      >
                        {weekDays.map(day => (
                          <option key={day.key} value={day.key}>
                            {day.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    
                    <div className="sentence-part">
                      <span className="sentence-text">entre</span>
                    </div>
                    
                    <div className="sentence-part">
                      <select 
                        value={timeRangeStart} 
                        onChange={(e) => setTimeRangeStart(e.target.value)}
                        className="form-select-inline"
                      >
                        {timeSlots.map(time => (
                          <option key={time} value={time}>{time}</option>
                        ))}
                      </select>
                    </div>
                    
                    <div className="sentence-part">
                      <span className="sentence-text">y</span>
                    </div>
                    
                    <div className="sentence-part">
                      <select 
                        value={timeRangeEnd} 
                        onChange={(e) => setTimeRangeEnd(e.target.value)}
                        className="form-select-inline"
                      >
                        {timeSlots.map(time => (
                          <option key={time} value={time}>{time}</option>
                        ))}
                      </select>
                    </div>
                    
                    <div className="sentence-part">
                      <span className="sentence-text">antes del</span>
                    </div>
                    
                    <div className="sentence-part">
                      <input 
                        type="date"
                        value={beforeDate} 
                        onChange={(e) => setBeforeDate(e.target.value)}
                        className="form-input-inline"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Opciones adicionales */}
              {staff && staff.length > 0 && (
                <div className="form-group">
                  <label htmlFor="preferred-staff">Profesional preferido (opcional):</label>
                  <select 
                    id="preferred-staff"
                    value={selectedStaff?.id || ''} 
                    onChange={(e) => {
                      const staffMember = staff.find(s => s.id === e.target.value);
                      setSelectedStaff(staffMember || null);
                    }}
                    className="form-select"
                  >
                    <option value="">Cualquier profesional</option>
                    {staff.map(staffMember => (
                      <option key={staffMember.id} value={staffMember.id}>
                        {staffMember.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {resources && resources.length > 0 && (
                <div className="form-group">
                  <label htmlFor="preferred-resource">Lugar preferido (opcional):</label>
                  <select 
                    id="preferred-resource"
                    value={selectedResource?.id || ''} 
                    onChange={(e) => {
                      const resource = resources.find(r => r.id === e.target.value);
                      setSelectedResource(resource || null);
                    }}
                    className="form-select"
                  >
                    <option value="">Cualquier lugar</option>
                    {resources.map(resource => (
                      <option key={resource.id} value={resource.id}>
                        {resource.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div className="step-actions">
                <button 
                  onClick={handleSubmitWaitingList} 
                  className="btn-primary"
                  disabled={isLoading || (
                    searchType === 'specific' 
                      ? (!preferredDate || !preferredTime)
                      : (!targetWeekday || !beforeDate || !timeRangeStart || !timeRangeEnd)
                  )}
                >
                  {isLoading ? 'Agregando a la lista...' : '📋 Unirme a la Lista de Espera'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default WaitingListModal; 